/**
 * Compact feed-card meta: severity icon + TYPE / timing (no Type/Severity/Timing labels).
 */
import { CheckCircle2 } from "lucide-react";

import { cn } from "@/lib/utils";
import type { FeedSeverity, TimeHorizon } from "@/types/feed";
import { HORIZON_BADGE, SEVERITY_ICON, SEVERITY_LABEL, SEVERITY_TEXT } from "./feedVisuals";

function MetaSep() {
  return (
    <span className="shrink-0 text-muted-foreground/35" aria-hidden>
      ·
    </span>
  );
}

/** Personal review stamp — circled tick, right-aligned under the relative time. */
export function FeedReviewedMark({ className }: { className?: string }) {
  return (
    <CheckCircle2
      className={cn(
        "feed-event-reviewed-tag size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400",
        className,
      )}
      role="img"
      aria-label="Reviewed"
    />
  );
}

export interface FeedEventCardMetaProps {
  typeLabel: string;
  severity: FeedSeverity;
  horizon?: TimeHorizon | null;
  /** Extra row state, e.g. High impact — shown unlabeled after type/timing. */
  statusLabel?: string | null;
  className?: string;
}

export function FeedEventCardMeta({
  typeLabel,
  severity,
  horizon,
  statusLabel,
  className,
}: FeedEventCardMetaProps) {
  const horizonMeta = horizon ? HORIZON_BADGE[horizon] : null;
  const SeverityIcon = SEVERITY_ICON[severity];

  return (
    <div
      className={cn(
        "feed-event-meta flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] leading-snug",
        className,
      )}
    >
      <SeverityIcon
        className={cn("size-3 shrink-0", SEVERITY_TEXT[severity])}
        role="img"
        aria-label={SEVERITY_LABEL[severity]}
      />
      <span className="min-w-0 truncate font-semibold uppercase tracking-wide">
        <span className="feed-event-meta__type">{typeLabel}</span>
        {horizonMeta && (
          <>
            <span className="mx-1 font-medium text-muted-foreground/45" aria-hidden>
              /
            </span>
            <span className="font-medium normal-case tracking-normal text-muted-foreground">
              {horizonMeta.label}
            </span>
          </>
        )}
      </span>
      {statusLabel && (
        <>
          <MetaSep />
          <span className="min-w-0 truncate font-medium text-muted-foreground">
            {statusLabel}
          </span>
        </>
      )}
    </div>
  );
}
